import type { Theme } from '@mui/material/styles'
import { chipTokens } from '../theme/tokens'

const CATEGORY_HUES = [
  4,
  24,
  36,
  48,
  82,
  138,
  164,
  192,
  212,
  232,
  266,
  312,
] as const

const hashSlug = (slug: string): number => {
  let hash = 0
  for (let index = 0; index < slug.length; index += 1) {
    hash = (hash << 5) - hash + slug.charCodeAt(index)
    hash |= 0
  }
  return Math.abs(hash)
}

const toHsl = (hue: number, saturation: number, lightness: number): string =>
  `hsl(${hue}, ${saturation}%, ${lightness}%)`

const toHsla = (hue: number, saturation: number, lightness: number, alpha: number): string =>
  `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`

const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  const saturation = s / 100
  const lightness = l / 100

  if (saturation === 0) {
    const gray = Math.round(lightness * 255)
    return [gray, gray, gray]
  }

  const hue = ((h % 360) + 360) % 360 / 360
  const q = lightness < 0.5 ? lightness * (1 + saturation) : lightness + saturation - lightness * saturation
  const p = 2 * lightness - q

  const hueToChannel = (t: number) => {
    let channel = t
    if (channel < 0) channel += 1
    if (channel > 1) channel -= 1
    if (channel < 1 / 6) return p + (q - p) * 6 * channel
    if (channel < 1 / 2) return q
    if (channel < 2 / 3) return p + (q - p) * (2 / 3 - channel) * 6
    return p
  }

  const r = Math.round(hueToChannel(hue + 1 / 3) * 255)
  const g = Math.round(hueToChannel(hue) * 255)
  const b = Math.round(hueToChannel(hue - 1 / 3) * 255)

  return [r, g, b]
}

const getContrastingText = (hue: number, saturation: number, lightness: number): string => {
  const [r, g, b] = hslToRgb(hue, saturation, lightness)
  const srgb = [r, g, b].map((value) => {
    const channel = value / 255
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]

  const luminance = 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2]
  return luminance > 0.52 ? '#1a1c1f' : '#ffffff'
}

export interface CategoryColorSet {
  hue: number
  solid: string
  solidHover: string
  selectedBorder: string
  inactiveBackground: string
  inactiveHoverBackground: string
  inactiveBorder: string
  inactiveHoverBorder: string
  accent: string
  textOnSolid: string
}

export const getCategoryColor = (slug: string): CategoryColorSet => {
  const paletteIndex = hashSlug(slug || 'prep') % CATEGORY_HUES.length
  const hue = CATEGORY_HUES[paletteIndex]

  const solidSaturation = 72
  const solidLightness = 44
  const solid = toHsl(hue, solidSaturation, solidLightness)
  const solidHover = toHsl(hue, 74, 38)
  const inactiveBackground = toHsla(hue, 78, 88, chipTokens.inactiveBgAlpha)
  const inactiveHoverBackground = toHsla(hue, 80, 86, chipTokens.inactiveHoverBgAlpha)
  const inactiveBorder = toHsla(hue, 68, 52, chipTokens.inactiveBorderAlpha)
  const inactiveHoverBorder = toHsla(hue, 68, 48, chipTokens.inactiveHoverBorderAlpha)
  const selectedBorder = toHsla(hue, 70, 42, chipTokens.selectedBorderAlpha)
  const accent = toHsl(hue, 70, 42)

  return {
    hue,
    solid,
    solidHover,
    selectedBorder,
    inactiveBackground,
    inactiveHoverBackground,
    inactiveBorder,
    inactiveHoverBorder,
    accent,
    textOnSolid: getContrastingText(hue, solidSaturation, solidLightness),
  }
}

export type ChipTextColor = string | ((theme: Theme) => string)

export interface ChipColors {
  background: string
  hoverBackground: string
  border: string
  hoverBorder: string
  text: ChipTextColor
}

export const getChipColors = (slug: string, options: { selected?: boolean } = {}): ChipColors => {
  const { selected = false } = options
  const colors = getCategoryColor(slug)

  if (selected) {
    return {
      background: colors.solid,
      hoverBackground: colors.solidHover,
      border: colors.selectedBorder,
      hoverBorder: colors.selectedBorder,
      text: colors.textOnSolid,
    }
  }

  return {
    background: colors.inactiveBackground,
    hoverBackground: colors.inactiveHoverBackground,
    border: colors.inactiveBorder,
    hoverBorder: colors.inactiveHoverBorder,
    text: (theme: Theme) =>
      theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary,
  }
}
